import { useDynamicConfig } from '@statsig/react-bindings';
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';

import { useDiscountContext } from '../_contexts/DiscountProvider';
import getDiscountMessage from '../_utils/discount/getDiscountMessage';
import type { UiDiscount } from '../_utils/discount/types';
import showDiscountMessage from '../_utils/showDiscountMessage';
import useFormattedDiscountPreview from './useFormattedDiscountPreview';

export interface UseDiscountMessageProps {
  isAccessory: boolean;
  isOuraRing4Wizard: boolean;
}
interface UseDiscountMessage {
  displayMessage: string | null | ReactNode;
}

const useDiscountMessage = ({
  isAccessory,
  isOuraRing4Wizard,
}: UseDiscountMessageProps): UseDiscountMessage => {
  const t = useTranslations();

  const sitewideDiscountConfig = useDynamicConfig('sitewide_discount');
  const sitewideDiscounts = (sitewideDiscountConfig.get('sitewideDiscount') ??
    '') as string | string[];

  const discountConfig = useDynamicConfig('discount_user_interface');
  const statsigDiscountArray = discountConfig.get(
    'discountArray',
    [],
  ) as UiDiscount[];

  const {
    appliedDiscountCode,
    discount,
    discountIsLoading,
    discountIsSuccess,
    promotionCodes,
    urlDiscount,
    urlDiscountIsSuccess,
    urlPromotionData,
  } = useDiscountContext();

  // determine whether or not to show discount eyebrow
  const showUrlDiscount = showDiscountMessage({
    isAccessory,
    isOuraRing4Wizard,
    urlDiscount,
    urlDiscountIsSuccess,
    urlPromotionData,
  });

  // get the appropriate discount message to display
  const discountMessage = getDiscountMessage({
    appliedDiscountCode,
    campaign: discount?.campaign,
    discount,
    discountIsLoading,
    discountIsSuccess,
    promotionCodes,
    showUrlDiscount,
    sitewideDiscounts,
    statsigDiscountArray,
    urlPromotionData,
  });

  // format price if applicable
  const { isLoading: formattedAmountIsLoading, formattedAmount } =
    useFormattedDiscountPreview(appliedDiscountCode);

  let displayMessage = null;

  // t() the Phrase string for display in multiple languages
  if (discountMessage) {
    if (!formattedAmountIsLoading) {
      displayMessage = t.rich(discountMessage, { amount: formattedAmount });
    } else {
      displayMessage = t(discountMessage);
    }
  }

  return { displayMessage };
};

export default useDiscountMessage;
